import { gql } from '@apollo/client'
import { ClientDashboardItem, Matter, Section } from 'share/domain/case'

export const GET_CLIENT_LEVEL_DASHBOARD = gql`
  query GET_CLIENT_LEVEL_DASHBOARD($active: Boolean!) {
    cases {
      clientLevelDashboard(active: $active) {
        clientDashboardItemList {
          id
          caseType
          lastVisitedDate
          status
          caseType
          attorneyName
          firmName
          openDate
        }
      }
    }
  }
`

export interface GetClientLevelDashboardVariables {
  active: boolean
}

export interface GetClientLevelDashboardResponse {
  cases: {
    clientLevelDashboard: {
      clientDashboardItemList: ClientDashboardItem[]
    }
  }
}

export const GET_CASE_LEVEL_DASHBOARD = gql`
  query GET_CASE_LEVEL_DASHBOARD($caseId: Int!) {
    cases {
      caseLevelDashboard(caseId: $caseId) {
        allDocumentCategoriesCompleteFlag
        case {
          id
          bccReference
          caseNumber
          accountId
          active
          attorneyId
          attorneyName
          attorneyAvatarUrl
          caseType
          lastVisitedDate
          status
          openDate
          statuses {
            name
            description
            current
          }
          isJoint
          debtor1FirstCourseForJointCaseOrders
          debtor1SecondCourseForJointCaseOrders
          debtor1EmailAddress
          debtor1FirstName
          debtor1LastName
          debtor2EmailAddress
          debtor2FirstName
          debtor2LastName
          isAiEnabled
          isChatBotEnabled
          isCompleteAllChecklistUploadedDocuments
          hasLegacyOrder
          clientCode
          organizationalUnitIdKey
          debtor1HasPlacedFirstCourseOrder
          debtor1HasPlacedSecondCourseOrder
          debtor1HasPlacedFirstCourseOrderStatus
          debtor1HasPlacedSecondCourseOrderStatus
          debtor1HasPlacedFirstCourseOrderStatusMessage
          debtor1HasPlacedSecondCourseOrderStatusMessage
          debtor1FirstCourseOrderStep
          debtor1SecondCourseOrderStep
          debtor2FirstCourseForJointCaseOrders
          debtor2SecondCourseForJointCaseOrders
          debtor2HasPlacedFirstCourseOrder
          debtor2HasPlacedSecondCourseOrder
          debtor2HasPlacedFirstCourseOrderStatus
          debtor2HasPlacedSecondCourseOrderStatus
          debtor2HasPlacedFirstCourseOrderStatusMessage
          debtor2HasPlacedSecondCourseOrderStatusMessage
          debtor2FirstCourseOrderStep
          debtor2SecondCourseOrderStep
          questionnaireDisplayInClientPortalFlag
          questionnaireExpectedCompletionDate
          questionnaireExpectedCompletionMonth
          questionnaireExpectedCompletionDay
          questionnaireExpectedCompletionYear
          questionnaireExpectedCompletionFormatted
          questionnaireInstructions
          questionnaireShownInstructionsFlag
          questionnaireShownTermsAndConditionsFlag
          firm {
            id
            name
            officeId
            emailAddress
            iconUrl
            street
            city
            state
            zip
            phone
            fax
            website
          }
        }
      }
    }
  }
`

export interface GetCaseLevelDashboardVariables {
  caseId: number
}

export interface GetCaseLevelDashboardResponse {
  cases: {
    caseLevelDashboard: {
      allDocumentCategoriesCompleteFlag: boolean
      case: Matter | null
    }
  }
}

export const GET_CASES_BY_ACTIVE = gql`
  query GET_CASES_BY_ACTIVE($active: Boolean!) {
    cases {
      get(where: { active: $active }) {
        id
        bccReference
        caseNumber
        accountId
        active
        attorneyId
        attorneyName
        attorneyAvatarUrl
        caseType
        lastVisitedDate
        status
        openDate
        isJoint
        debtor1FirstCourseForJointCaseOrders
        debtor1SecondCourseForJointCaseOrders
        debtor1EmailAddress
        debtor1FirstName
        debtor1LastName
        debtor2EmailAddress
        debtor2FirstName
        debtor2LastName
        isAiEnabled
        isChatBotEnabled
        isCompleteAllChecklistUploadedDocuments
        hasLegacyOrder
        clientCode
        organizationalUnitIdKey
        debtor1HasPlacedFirstCourseOrder
        debtor1HasPlacedSecondCourseOrder
        debtor1HasPlacedFirstCourseOrderStatus
        debtor1HasPlacedSecondCourseOrderStatus
        debtor1HasPlacedFirstCourseOrderStatusMessage
        debtor1HasPlacedSecondCourseOrderStatusMessage
        debtor1FirstCourseOrderStep
        debtor1SecondCourseOrderStep
        debtor2FirstCourseForJointCaseOrders
        debtor2SecondCourseForJointCaseOrders
        debtor2HasPlacedFirstCourseOrder
        debtor2HasPlacedSecondCourseOrder
        debtor2HasPlacedFirstCourseOrderStatus
        debtor2HasPlacedSecondCourseOrderStatus
        debtor2HasPlacedFirstCourseOrderStatusMessage
        debtor2HasPlacedSecondCourseOrderStatusMessage
        debtor2FirstCourseOrderStep
        debtor2SecondCourseOrderStep
        questionnaireDisplayInClientPortalFlag
        questionnaireExpectedCompletionDate
        questionnaireExpectedCompletionMonth
        questionnaireExpectedCompletionDay
        questionnaireExpectedCompletionYear
        questionnaireExpectedCompletionFormatted
        questionnaireInstructions
        questionnaireShownInstructionsFlag
        questionnaireShownTermsAndConditionsFlag
        firm {
          id
          name
          officeId
          emailAddress
          iconUrl
          street
          city
          state
          zip
          phone
          fax
          website
        }
      }
    }
  }
`

export interface GetCasesVariables {
  active: boolean
}

export const GET_CASE_QUESTIONNAIRE_SECTIONS = gql`
  query GET_CASE_QUESTIONNAIRE_SECTIONS($caseId: Int!) {
    cases {
      caseQuestionnaireSections(caseId: $caseId) {
        id
        sectionSystemName
        sectionName
        sectionStatus
        sectionDescription
        sectionOrder
        version
        comments
        currentActiveQuestionId
        isRejected
        sectionCardSummary
        isDisplayEnabled
        sectionDataRecords {
          id
          sectionId
          questionKey
          questionDataJsonText
          questionCompleteFlag
        }
      }
    }
  }
`

export interface GetCaseQuestionnaireSectionsVariables {
  caseId: number
}

export interface GetCaseQuestionnaireSectionsResponse {
  cases: {
    caseQuestionnaireSections: Section[]
  }
}

export const GET_CASE_QUESTIONNAIRE_SECTION = gql`
  query GET_CASE_QUESTIONNAIRE_SECTION($caseId: Int!, $sectionId: Int!) {
    cases {
      caseQuestionnaireSectionById(caseId: $caseId, sectionId: $sectionId) {
        id
        sectionSystemName
        sectionName
        sectionStatus
        sectionDescription
        sectionOrder
        version
        comments
        currentActiveQuestionId
        isRejected
        sectionCardSummary
        isDisplayEnabled
        sectionDataRecords {
          id
          sectionId
          questionKey
          questionDataJsonText
          questionCompleteFlag
        }
      }
    }
  }
`

export interface GetCaseQuestionnaireSectionVariables {
  caseId: number
}

export interface GetCaseQuestionnaireSectionResponse {
  cases: {
    caseQuestionnaireSectionById: Section
  }
}
